import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../state'
import * as messageInput from '../input'

import * as emptyState from './empty-state'
import * as loader from './loader'
import * as message from './message'

interface AssistantSection {
  readonly assistant: ents.core.agent.Display
  readonly conversations: readonly ents.core.agent.Conversation[]
  readonly expanded: boolean
}

/**
 * Renders conversational assistant history, messages, and input controls.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.text.Component,
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.overlay.confirmation.Component,
    emptyState.Component,
    loader.Component,
    message.Component,
    messageInput.Component,
    ngCommon.DatePipe,
    ngCommon.NgTemplateOutlet,
  ],
  selector: 'atn-assistant-conversational',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.assistants.Assistants} */
  public readonly assistants = ngCore.inject(state.assistants.Assistants)

  /** Whether history and chat should be shown side-by-side. */
  public readonly fullScreenSg = ngCore.input(false, { alias: 'fullScreen' })

  /**
   * The conversational assistant instance, derived from Assistants service
   */
  public readonly groupSg = ngCore.computed(() => {
    const group = this.assistants.selectedConversationalGroupSg()
    if (!group) {
      throw new Error('Expected conversational assistant')
    }
    return group
  })

  /** @see {@link state.fileMgmt.Feature} */
  public readonly fileMgmtFeature = ngCore.inject(state.fileMgmt.Feature)
  /** @see {@link state.fileMgmt.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /**
   * Question being written by the user. Allows us to reset after it has been submitted
   */
  public readonly questionSg = ngCore.signal('')

  /** Messages container element reference for auto-scrolling */
  private readonly _messagesContainerRef =
    ngCore.viewChild<ngCore.ElementRef<HTMLDivElement>>('messagesContainer')

  /** Tracks manual fullscreen assistant history expansion overrides. */
  private readonly _expandedAssistantSectionsSg = ngCore.signal<
    Partial<Record<ents.core.agent.Agent.Id, boolean>>
  >({})

  /**
   * Gets conversations for the current agent
   */
  public readonly currentAgentConversationsSg = ngCore.computed(
    () =>
      this.groupSg()
        .getAgentGroup(this.groupSg().selectedAgentIdSg())
        .asSignal()?.entities ?? [],
  )

  /**
   * Groups available assistants with their conversation history for fullscreen.
   */
  public readonly fullscreenAssistantSectionsSg = ngCore.computed<
    readonly AssistantSection[]
  >(() => {
    if (!this.fullScreenSg()) {
      return []
    }

    const expandedSections = this._expandedAssistantSectionsSg()
    const selectedAgentId = this.assistants.selectedAgentIdSg()

    return this.assistants.availableAssistantDisplaysSg().map(assistant => {
      const expanded =
        expandedSections[assistant.id] ?? assistant.id === selectedAgentId
      const assistantGroup = expanded
        ? this.assistants.getConversationalGroupByAgentId(assistant.id)
        : undefined

      return {
        assistant,
        conversations:
          assistantGroup?.getAgentGroup(assistant.id).asSignal()?.entities ??
          [],
        expanded,
      }
    })
  })

  /**
   * Gets the current conversation
   */
  public readonly currentConversationSg = ngCore.computed(() => {
    const assistant = this.groupSg()
    return assistant.conversation.plugins.currentEntityPlugin.feature.selectors
      .getEntity()
      .asSignal()
  })

  /**
   * Gets messages for the current conversation, each with the entries it is
   * made of.
   */
  public readonly messagesSg = ngCore.computed(
    () =>
      this.groupSg()
        .messages.plugins.lookup(state.assistant.conversational.messages.plugins.sender.Plugin)
        .get()
        .feature.selectors.messagesWithInfo.asSignal() ?? [],
  )

  /**
   * Checks if a message is currently being sent
   */
  public readonly isSubmittingSg = ngCore.computed(() => {
    const assistant = this.groupSg()
    const senderPlugin = assistant.messages.plugins
      .lookup(state.assistant.conversational.messages.plugins.sender.Plugin.id)
      .get()
    // TODO
    return false
  })

  /**
   * Gets any error from sending a message
   */
  public readonly errorSg = ngCore.computed(
    (
      error = this.groupSg().messages.plugins.currentListPlugin.feature.selectors.error.asSignal(),
    ) => error?.message,
  )

  /**
   * Effect to auto-scroll to bottom when new messages are added
   */
  private readonly _autoScrollEffect = ngCore.effect(() => {
    // we need to read messagesSg to trigger on new messages / message updates
    this.messagesSg()

    // at this moment we know messages will be shown
    if (!this.showLoaderSg()) {
      requestAnimationFrame(() => {
        this._scrollToBottom()
      })
    }
  })

  /**
   * Controls whether the conversation history sidebar is expanded
   */
  public readonly presentConversationSg = ngCore.signal(false)

  /**
   * Gets the currently selected conversation ID from the assistant state
   */
  public readonly selectedConversationIdSg = ngCore.computed(() => {
    const assistant = this.groupSg()
    return assistant.conversation.plugins.currentEntityPlugin.feature.selectors
      .getId()
      .asSignal()
  })

  /**
   * Tracks whether the loader should be displayed.
   * Resets to true when switching to an existing conversation.
   * Stays false for new conversations (no ID).
   * Hides after 1s delay when loading completes.
   */
  public readonly showLoaderSg = ngCore.linkedSignal({
    computation: (source: unknown) => !!source,
    source: this.selectedConversationIdSg,
  })

  /** Effect to manage loader visibility with minimum duration */
  private readonly _loaderEffect = ngCore.effect(onCleanup => {
    const assistant = this.groupSg()
    const isConversationLoading =
      assistant.conversation.plugins.currentListPlugin.feature.selectors.isLoading.asSignal()
    const isMessagesLoading =
      assistant.messages.plugins.currentListPlugin.feature.selectors.isLoading.asSignal()

    if (isConversationLoading || isMessagesLoading) {
      this.showLoaderSg.set(true)
    } else {
      const timeout = setTimeout(() => this.showLoaderSg.set(false), 1000)
      onCleanup(() => clearTimeout(timeout))
    }
  })

  /**
   * Toggle sidebar expanded/collapsed state
   */
  public toggleSidebar(): void {
    this.presentConversationSg.update(expanded => !expanded)
  }

  /**
   * Select a conversation from the history
   */
  public selectConversation(conversationId: unknown | undefined): void {
    this.groupSg()
      .conversation.plugins.currentEntityPlugin.storage.actions.putInIdLookup.from(
        'Assistant.Conversation.Selection.Changed',
      )
      .dispatch({
        value: TypeChecker.isString(conversationId)
          ? conversationId
          : undefined,
      })
  }

  /**
   * Selects a new conversation from a history surface.
   */
  public selectNewConversation(closeHistory: boolean): void {
    if (closeHistory) {
      this.presentConversationSg.set(false)
    }
    this.selectConversation(undefined)
  }

  /**
   * Selects a new conversation for a specific agent.
   */
  public selectNewConversationForAgent(
    agentId: ents.core.agent.Agent.Id,
    closeHistory: boolean,
  ): void {
    this.selectAgentConversation(agentId, undefined, closeHistory)
  }

  /**
   * Selects an existing conversation from a history surface.
   */
  public selectConversationFromHistory(
    conversationId: ents.core.agent.Conversation.Id,
    closeHistory: boolean,
  ): void {
    if (closeHistory) {
      this.presentConversationSg.set(false)
    }
    this.selectConversation(conversationId)
  }

  /**
   * Selects an existing conversation for a specific agent.
   */
  public selectAgentConversationFromHistory(
    agentId: ents.core.agent.Agent.Id,
    conversationId: ents.core.agent.Conversation.Id,
    closeHistory: boolean,
  ): void {
    this.selectAgentConversation(agentId, conversationId, closeHistory)
  }

  /**
   * Toggles a fullscreen assistant conversation section.
   */
  public toggleAssistantSection(agentId: ents.core.agent.Agent.Id): void {
    const selectedAgentId = this.assistants.selectedAgentIdSg()
    this._expandedAssistantSectionsSg.update(sections => ({
      ...sections,
      [agentId]: !(sections[agentId] ?? agentId === selectedAgentId),
    }))
  }

  /**
   * Selects an agent before changing its active conversation.
   */
  public selectAgentConversation(
    agentId: ents.core.agent.Agent.Id,
    conversationId: ents.core.agent.Conversation.Id | undefined,
    closeHistory: boolean,
  ): void {
    if (closeHistory) {
      this.presentConversationSg.set(false)
    }
    this.assistants.selectAgent(agentId)
    this.selectConversation(conversationId)
  }

  /**
   * Mark a conversation for deletion (shows confirmation dialog)
   */
  public deleteConversation(
    conversationId: ents.core.agent.Conversation.Id,
    event: Event,
  ): void {
    event.stopPropagation()
    this.groupSg()
      .conversation.plugins.editingPlugin.feature.actions.markForDeletion.from(
        'Assistant.Conversation.deleteConversation',
      )
      .dispatch({
        id: conversationId,
      })
  }

  /**
   * Marks a conversation for deletion after activating its agent.
   */
  public deleteAgentConversation(
    agentId: ents.core.agent.Agent.Id,
    conversationId: ents.core.agent.Conversation.Id,
    event: Event,
  ): void {
    event.stopPropagation()
    this.assistants.selectAgent(agentId)
    this.deleteConversation(conversationId, event)
  }

  /**
   * Confirm deletion of the marked conversation
   */
  public confirmDeleteConversation(): void {
    this.groupSg()
      .conversation.plugins.editingPlugin.feature.actions.submit.from(
        'Assistant.Conversation.Confirmation.Confirm.Clicked',
      )
      .dispatch({ delete: true })
  }

  /**
   * Cancel deletion of the marked conversation
   */
  public cancelDeleteConversation(): void {
    this.groupSg()
      .conversation.plugins.editingPlugin.feature.actions.discard.from(
        'Assistant.Conversation.Confirmation.Cancel.Clicked',
      )
      .dispatch({ delete: true })
  }

  /**
   * Edit a conversation name
   */
  public editConversation(
    conversationId: ents.core.agent.Conversation.Id,
    event: Event,
  ): void {
    event.stopPropagation()
    this.groupSg()
      .conversation.plugins.editingPlugin.feature.actions.edit.from(
        'Assistant.Conversation.Edit.Clicked',
      )
      .dispatch({ id: conversationId })
  }

  /**
   * Starts editing a conversation after activating its agent.
   */
  public editAgentConversation(
    agentId: ents.core.agent.Agent.Id,
    conversationId: ents.core.agent.Conversation.Id,
    event: Event,
  ): void {
    event.stopPropagation()
    this.assistants.selectAgent(agentId)
    this.editConversation(conversationId, event)
  }

  /**
   * Update conversation name
   */
  public updateConversationName(name: string): void {
    this.groupSg()
      .conversation.plugins.editingPlugin.feature.actions.edit.from(
        'Assistant.Conversation.Name.Inputted',
      )
      .dispatch({
        edit: conversation => {
          if (!conversation) {
            return
          }
          conversation.name = name
        },
      })
  }

  /**
   * Save conversation changes
   */
  public saveConversation(): void {
    this.groupSg()
      .conversation.plugins.editingPlugin.feature.actions.submit.from(
        'Assistant.Conversation.SaveButton.Clicked',
      )
      .dispatch({
        current: true,
        skipUnset: true,
      })
  }

  /**
   * Cancel conversation edit
   */
  public cancelEditConversation(): void {
    this.groupSg()
      .conversation.plugins.editingPlugin.feature.actions.discard.from(
        'Assistant.Conversation.CancelButton.Clicked',
      )
      .dispatch({
        current: true,
        skipUnset: true,
      })
  }

  /**
   * Submit the current message
   */
  public submitMessage({
    content,
    files,
    knowledgeBaseRetrieval,
  }: {
    content: string
    files: File[]
    knowledgeBaseRetrieval?: readonly ents.core.agent.KnowledgeBase.Retrieval.Record[]
  }): void {
    const question = content.trim()
    if (!question) {
      return
    }

    const conversationId = this.groupSg()
      .conversation.plugins.currentEntityPlugin.feature.selectors.getId()
      .asSignal()
    const assertIsPartialIdOrUndefined: TypeAsserter<PartialId | undefined> =
      TypeAsserter.Factory.from(
        TypeChecker.Factory.optional(PartialId.isPartialId),
      )
    assertIsPartialIdOrUndefined(conversationId)

    // Clear input
    this.questionSg.set('')
    this.groupSg()
      .messages.plugins.lookup(
        state.assistant.conversational.messages.plugins.sender.Plugin,
      )
      .get()
      .feature.actions.sendMessage.from('Assistant.Conversation.submitMessage')
      .dispatch({
        agentId: this.groupSg().selectedAgentIdSg(),
        content: question,
        conversationId,
        files,
        knowledgeBaseRetrieval: knowledgeBaseRetrieval?.length
          ? { records: knowledgeBaseRetrieval }
          : undefined,
      })
  }

  /**
   * Scroll messages container to bottom
   */
  private _scrollToBottom(): void {
    const container = this._messagesContainerRef()?.nativeElement
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }

  /**
   * Requests a signed download for a file attached to an assistant message.
   */
  public fileDownloadClickHandler(event: {
    message: ents.core.agent.Message
    file: ents.core.agent.Message.File
  }): void {
    const conversationId = this.groupSg()
      .conversation.plugins.currentEntityPlugin.feature.selectors.getId()
      .asSignal()
    const context = this.meFeature.contexts.selectors.zoneContext.asSignal()
    if (!conversationId || !context) {
      return
    }
    const { extension, name: id } =
      state.fileMgmt.models.FileRequest.getFileInfo(event.file)
    this.fileMgmtFeature.actions.download
      .from('Assistant.Conversational.Message.FileDownloadClicked')
      .dispatch({
        context,
        extension,
        id,
        purpose: ents.core.common.fileManagement.Purpose.FileManagement,
        qualifier: ents.core.agent.Message.File.generateQualifier({
          conversation: conversationId as PartialId,
          message: event.message,
        }),
      })
  }
}
